import {
    type FieldPath,
    type FieldValues,
    type UseFormReturn,
    useController,
} from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { FieldError } from '@gitbook/spine-react';

import { LocalizedTextInput, type LocalizedTextInputProps } from './LocalizedTextInput';

export type LocalizedTextFieldProps<TFieldValues extends FieldValues> = Omit<
    LocalizedTextInputProps,
    'value' | 'localizedValue' | 'onChange'
> & {
    name: FieldPath<TFieldValues>;
    localizedName: FieldPath<TFieldValues>;
    form: UseFormReturn<TFieldValues>;
    /** Callback when the value or a localized override changes. */
    onChange?: (props: { value: string; localizedValue: api.LocalizedString }) => void;
};

/**
 * Form-field wrapper around LocalizedTextInput.
 * Reads from and writes to react-hook-form fields via useController.
 */
export function LocalizedTextField<TFieldValues extends FieldValues>(
    props: LocalizedTextFieldProps<TFieldValues>
) {
    const { name, localizedName, form, enabled = true, onChange, inputProps, ...rest } = props;
    const { required, minLength, maxLength } = inputProps ?? {};

    const {
        field: fallbackField,
        fieldState: { error },
    } = useController({
        name,
        control: form.control,
        rules: {
            required: required ? 'Required' : false,
            minLength:
                minLength !== undefined
                    ? {
                          value: minLength,
                          message: `Minimum ${minLength} character${minLength > 1 ? 's' : ''}`,
                      }
                    : undefined,
            maxLength:
                maxLength !== undefined
                    ? {
                          value: maxLength,
                          message: `Maximum ${maxLength} character${maxLength > 1 ? 's' : ''}`,
                      }
                    : undefined,
        },
    });
    const { field: localizedField } = useController({
        name: localizedName,
        control: form.control,
    });

    const fallbackValue = (fallbackField.value as string) ?? '';
    const localizedValue = (localizedField.value as api.LocalizedString) ?? {};

    return (
        <>
            <LocalizedTextInput
                {...rest}
                enabled={enabled}
                value={fallbackValue}
                localizedValue={localizedValue}
                inputProps={inputProps}
                onChange={({ value, localizedValue }) => {
                    fallbackField.onChange(value);
                    localizedField.onChange(localizedValue);
                    onChange?.({ value, localizedValue });
                }}
            />
            {error ? <FieldError>{error.message}</FieldError> : null}
        </>
    );
}
